import { RestClientAuthenticated } from '../network';
import { EmailsDTO } from './types/EmailsDTO';
import { DownloadAppUrlDTO } from './types/compte/DownloadAppUrlDTO';
import { PairingCodeDTO } from './types/compte/PairingCodeDTO';
import { CompteProfilProModifyDTO } from './types/compte/CompteProfilProModifyDTO';
import { CompteMonidentiteModifyDTO } from './types/compte/CompteMonidentiteModifyDTO';
import { LangsDTO } from './types/LangsDTO';
import { SimpleDTO } from './types/SimpleDTO';
import { LocationsDTO } from './types/compte/LocationsDTO';
import { LocationDTO } from './types/compte/LocationDTO';
import { MonIdentite } from './types/compte/MonIdentite';
import { UserDTO } from './types/compte/UserDTO';
import { PairedDevicesDTO } from './types/compte/PairedDevicesDTO';
import { InWeboDevice } from './types/compte/InWeboDevice';
import { SingleFieldDTOString } from './types/SingleFieldDTOString';
import { ResponseDTO } from './types/ResponseDTO';
import { CompteDashboardContactDTO } from './types/compte/CompteDashboardContactDTO';
import { EntityUpdateDTO } from './types/compte/EntityUpdateDTO';
import { MemberCount } from './types/compte/MemberCount';
import { ListMember } from './types/compte/ListMember';
import { CompteProfilProProfileDetails } from './types/compte/CompteProfilProProfileDetails';
import { MemberPersonalInfos } from './types/compte/MemberPersonalInfos';
import { CompteEntitesMemberProfessionalInfosModifyDTO } from './types/compte/CompteEntitesMemberProfessionalInfosModifyDTO';
import { UserHelpDTO } from './types/UserHelpDTO';
import { PasswordChangeDTO } from './types/compte/PasswordChangeDTO';
import { NotificationList } from './types/compte/Notification';
import { SingleFieldDTOInteger } from './types/SingleFieldDTOInteger';

const compteApi = {
  readNotification: (): Promise<ResponseDTO> => new RestClientAuthenticated('/notifications', 'PUT', null)
    .execute(),
  changePseudo: (pseudo: SingleFieldDTOString): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/parametres/pseudo/change', 'POST', 'application/json')
    .jsonBody(pseudo)
    .execute(),
  changePassword: (passwordChangeDTO: PasswordChangeDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/password/change', 'POST', 'application/json')
    .jsonBody(passwordChangeDTO)
    .execute(),
  dashboardContact: (compteDashboard: CompteDashboardContactDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/dashboard/contact', 'POST', 'application/json')
    .jsonBody(compteDashboard)
    .execute(),
  fetchSubjectList: (): Promise<string[]> => new RestClientAuthenticated('/moncompte/dashboard/contact/getsubjectlist', 'GET', 'application/json')
    .execute(),
  modifyEntite: (entity: EntityUpdateDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/entite/modify', 'POST', 'application/json')
    .jsonBody(entity)
    .execute(),
  getMemberCount: (entityIdn: string): Promise<MemberCount> => new RestClientAuthenticated('/moncompte/entites/membercount', 'POST', 'application/json')
    .jsonBody({ idnEntite: entityIdn })
    .execute(),
  getMemberList: (rattachementIdn: string): Promise<ListMember> => new RestClientAuthenticated('/moncompte/entites/memberlist', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  fetchMyIdentity: (): Promise<MonIdentite> => new RestClientAuthenticated('/moncompte/monidentite', 'GET', 'application/json')
    .execute(),
  fetchNotification: (): Promise<NotificationList> => new RestClientAuthenticated('/notifications', 'GET', 'application/json')
    .execute(),
  fetchMyUserDetails: (): Promise<UserDTO> => new RestClientAuthenticated('/account', 'GET', 'application/json')
    .execute(),
  fetchMyPairedDevices: (): Promise<PairedDevicesDTO> => new RestClientAuthenticated('/inwebo/userdevices', 'GET', 'application/json')
    .execute(),
  fetchAppUrls: (): Promise<DownloadAppUrlDTO> => new RestClientAuthenticated('/inwebo/getappurls', 'GET', 'application/json')
    .execute(),
  checkMyReinforcedAuth: (): Promise<boolean> => new RestClientAuthenticated('/inwebo/ishardauth', 'GET', 'application/json')
    .execute(),
  setMyReinforcedAuth: (hardAuth: boolean): Promise<boolean> => new RestClientAuthenticated('/inwebo/sethardauth', 'POST', 'application/json')
    .jsonBody(hardAuth)
    .execute(),
  fetchMyQRCodes: (): Promise<PairingCodeDTO> => new RestClientAuthenticated('/inwebo/getpairingcode', 'GET', 'application/json')
    .execute(),
  fetchAllLangs: (): Promise<LangsDTO> => new RestClientAuthenticated('/getLangs', 'POST', 'application/json', true)
    .execute(),
  modifyMyIdentity: (details: CompteMonidentiteModifyDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/monidentite/modify', 'POST', 'application/json')
    .jsonBody(details)
    .execute(),
  modifyProfilPro: (infos: CompteProfilProModifyDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/profilpro/modify', 'POST', 'application/json')
    .jsonBody(infos)
    .execute(),
  dissociateDevice: (device: InWeboDevice): Promise<unknown> => new RestClientAuthenticated('/inwebo/deleteuserdevice', 'POST', 'application/json')
    .jsonBody(device)
    .execute(),
  saveEmergencyInfo: (emails: EmailsDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/parametres/infossecours/modify/save', 'POST', 'application/json')
    .jsonBody(emails)
    .execute(),
  fetchLocations: (entityID: SimpleDTO): Promise<LocationsDTO> => new RestClientAuthenticated('/moncompte/entites/entite/locations', 'POST', 'application/json')
    .jsonBody(entityID)
    .execute(),
  updateLocation: (location: LocationDTO): Promise<LocationDTO> => new RestClientAuthenticated('/moncompte/entites/entite/locations/update', 'POST', 'application/json')
    .jsonBody(location)
    .execute(),
  leaveEntite: (rattachementIdn: string): Promise<LocationDTO> => new RestClientAuthenticated('/moncompte/entites/leave', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  getDomainesDactivite: (): Promise<string[]> => new RestClientAuthenticated('/getDomainesDactivite', 'GET', 'application/json', true)
    .execute(),
  blockProfil: (rattachementIdn: string): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/bloquerprofil', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  unBlockProfil: (rattachementIdn: string): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/debloquerprofil', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  addDelegation: (rattachementIdn: string): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/membergestiondroitsmodify', 'POST', 'application/json')
    .jsonBody({ profileID: rattachementIdn, delegatedManager: true })
    .execute(),
  removeDelegation: (rattachementIdn: string): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/membergestiondroitsmodify', 'POST', 'application/json')
    .jsonBody({ profileID: rattachementIdn, delegatedManager: false })
    .execute(),
  deleteFromEntite: (rattachementIdn: string): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/supprimerprofil', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  getProfilePro: (rattachementIdn: string): Promise<CompteProfilProProfileDetails> => new RestClientAuthenticated('/moncompte/profilpro/profiledetail', 'POST', 'application/json')
    .jsonBody({ idnRattachement: rattachementIdn })
    .execute(),
  fetchEntityMemberPersonalInfos: (idnMembre: string, entityID: string): Promise<MemberPersonalInfos> => new RestClientAuthenticated('/moncompte/entites/memberpersonalinfos', 'POST', 'application/json')
    .jsonBody({ idnMembre, entityID })
    .execute(),
  modifyMemberProInfo: (dto: CompteEntitesMemberProfessionalInfosModifyDTO): Promise<ResponseDTO> => new RestClientAuthenticated('/moncompte/entites/memberprofessionalinfosmodify', 'POST', 'application/json')
    .jsonBody(dto)
    .execute(),
  getConnectedUserHelp: (): Promise<UserHelpDTO> => new RestClientAuthenticated('/connectedUserHelp', 'GET', 'application/json', true)
    .execute(),
  getCollaborateurStatut: (Compte_rattachement_DTO:{ searchCode: string, idn: string, entite: string }): Promise<SingleFieldDTOInteger> => new RestClientAuthenticated('/moncompte/entites/collaborateurstatut', 'POST', 'application/json')
    .jsonBody(Compte_rattachement_DTO)
    .execute(),
};

export default compteApi;
